import { GapRentalTable } from "@/app/GapRentals/Components/GapRentalTable";
import { RelocationStatus, RelocationType } from "@/gql/graphql";
import type { TableId } from "@/app/Common/Utils/table-id";

import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { GapRentalCreateDialog } from "@/app/GapRentals/Components/GapRentalCreateDialog";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { relocationStatusCountsQuery } from "@/app/Relocations/GraphQL/relocationStatusCountsQuery";
import { Button } from "@/components/catalyst/button";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
  Navbar,
  NavbarItem,
  NavbarSection,
} from "@/components/catalyst/navbar";
import { Badge, BadgeButton, BadgeColor } from "@/components/catalyst/badge";
import { GapRentalInfoDialog } from "@/app/GapRentals/Components/GapRentalInfoDialog";
import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { useLocalStorage } from "@/lib/Hooks/useLocalStorage";
import { ReactNode, useEffect } from "react";
import { useTranslations } from "use-intl";
import { RelocationTableState } from "@/app/Relocations/Utils/relocationTableState";

const GAP_RENTAL_INFO_SEEN_KEY = "gap-rental-info-seen";

export function GapRentalMultiTable({
  supplierId,
  state,
  onStateChange,
  tableId,
  title,
}: {
  supplierId?: string;
  state: RelocationTableState;
  onStateChange: (newState: RelocationTableState) => void;
  tableId: TableId;
  title?: string;
}) {
  const { open } = useDialog(GapRentalCreateDialog);
  const t = useTranslations("relocation.table");

  return (
    <div>
      <Header
        supplierId={supplierId}
        onStatusChange={(statuses) =>
          onStateChange({ ...state, statuses, page: 1 })
        }
        statuses={state.statuses}
        title={title}
      />
      <GapRentalTable
        id={tableId}
        supplierId={supplierId}
        state={state}
        onStateChange={onStateChange}
        rightButtons={
          <Button
            LeadingIcon={PlusIcon}
            onClick={() => {
              open({
                initialSupplierId: supplierId,
              });
            }}
          >
            {t("addGapRental")}
          </Button>
        }
      />
    </div>
  );
}

function Header({
  supplierId,
  onStatusChange,
  statuses,
  title,
}: {
  supplierId?: string;
  onStatusChange: (status: RelocationStatus[]) => void;
  statuses: RelocationStatus[];
  title?: string;
}) {
  const { data } = useGqlQuery(relocationStatusCountsQuery, {
    supplierId: supplierId ?? undefined,
    type: [RelocationType.GapRental],
  });
  const t = useTranslations("relocation.table");
  const tInfo = useTranslations("relocation.gapRentalInfo");
  const { open } = useDialog(GapRentalInfoDialog);
  const [hasSeenInfo, setHasSeenInfo] = useLocalStorage(
    GAP_RENTAL_INFO_SEEN_KEY,
    false,
  );

  useEffect(() => {
    if (!hasSeenInfo) {
      open({});
      setHasSeenInfo(true);
    }
  }, [hasSeenInfo, open, setHasSeenInfo]);

  return (
    <div className="relative border-gray-200 pb-5 sm:border-b sm:pb-0">
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold leading-6 text-gray-900">
            {title ?? t("gapRentalsTitle")}
          </h3>
          <BadgeButton color="emerald" onClick={() => open({})}>
            <InformationCircleIcon className="h-3.5 w-3.5" />
            {tInfo("badge")}
          </BadgeButton>
        </div>
      </div>

      <Navbar className="-mb-px">
        <NavbarSection className="max-lg:hidden" id="gap-rental-navbar">
          {getFilters(t).map(
            (
              { label, statuses: filterStatuses, hideBadge, badgeColor },
              idx,
            ) => {
              const count = filterStatuses.reduce((acc, status) => {
                return (
                  acc +
                  (data?.relocationStatusCounts.find(
                    (counts) => counts.status === status,
                  )?.count ?? 0)
                );
              }, 0);

              const isCurrent =
                statuses?.length === filterStatuses.length &&
                filterStatuses.every((status) => statuses?.includes(status)) &&
                statuses?.every((status) => filterStatuses.includes(status));

              return (
                <NavbarItem
                  key={idx}
                  onClick={() => {
                    onStatusChange?.(filterStatuses);
                  }}
                  current={isCurrent}
                >
                  {label}
                  {count && !hideBadge ? (
                    <Badge className="py-0" color={badgeColor}>
                      {count}
                    </Badge>
                  ) : null}
                </NavbarItem>
              );
            },
          )}
        </NavbarSection>
      </Navbar>
    </div>
  );
}

type Filter = {
  label: ReactNode;
  statuses: RelocationStatus[];
  hideBadge?: boolean;
  badgeColor?: BadgeColor;
};

function getFilters(t: (key: string) => string): Filter[] {
  return [
    {
      label: t("activeAndPaused"),
      statuses: [RelocationStatus.Ready, RelocationStatus.Paused],
      badgeColor: "green",
    },
    {
      label: t("soldOut"),
      statuses: [RelocationStatus.SoldOut],
      badgeColor: "yellow",
    },
    {
      label: t("expired"),
      statuses: [RelocationStatus.Expired],
      badgeColor: "red",
    },
    {
      label: t("draft"),
      statuses: [RelocationStatus.Draft],
    },
    {
      label: t("archived"),
      statuses: [RelocationStatus.Archived],
      hideBadge: true,
    },
  ];
}
